import { useState } from 'react';
import { AppBar, Toolbar, Box, Typography, TextField, InputAdornment, Button, Badge,
         Popover, Stack, Chip, MenuItem, Select, FormControl, InputLabel, IconButton, Tooltip, Divider,
         Menu, ListItemIcon, ListItemText, Avatar, SvgIcon, useMediaQuery } from '@mui/material';
import { Search, FilterList, IosShare, CancelPresentation, Refresh,
         Logout, LockOutlined, PeopleAltOutlined, DeleteSweepOutlined } from '@mui/icons-material';
import { CONCESSIONARIE, SORTS } from '../lib/parse';
import { wsurl } from '../Common';

const TAGS = ['CIMASA', 'NO CIMASA', 'LEDWALL', 'DIGITALE', 'ILLUMINATO'];

const BLU = '#1d4ed8';
const ARANCIO = '#FD5900';
const BORDO = '#e2e8f0';

/**
 * Marchio in barra. L'icona completa (con la strada) sotto i 48 px diventa una
 * macchia: su telefono, dove c'è posto solo per un quadrato da 32 px, si usa la
 * versione semplificata; su desktop il logo orizzontale con la scritta.
 */
function Logo({ compatto }) {
  return compatto
    ? <img src="/favicon.svg" width="32" height="32" alt="AffariAffissioni" style={{ display: 'block' }} />
    : <img src="/logo-orizzontale.png" alt="AffariAffissioni" style={{ display: 'block', height: 40, width: 'auto' }} />;
}

/** Foglio di calcolo verde per "Excel selezionati"; sbiadito quando non c'è niente da esportare. */
function IconaFoglio({ spento }) {
  return (
    <SvgIcon sx={{ opacity: spento ? 0.4 : 1 }}>
      <rect x="3" y="3" width="18" height="18" rx="3" fill="#1D6F42" />
      <path d="M7 9.5h10M7 13.5h10M11.5 7v11" stroke="#fff" strokeWidth="1.6" fill="none" />
    </SvgIcon>
  );
}

// Dal mockup: tutti i controlli della barra alti 40 px, angoli di 8 px, bordo chiaro
const CONTROLLO = { height: 40, borderRadius: '8px' };   // in px: il tema moltiplica i numeri
const BOTTONE = { ...CONTROLLO, px: 1.75, fontWeight: 600, whiteSpace: 'nowrap', borderColor: BORDO, color: BLU,
                  '&:hover': { borderColor: '#bfdbfe', bgcolor: '#f8fafc' } };
const BADGE = { '& .MuiBadge-badge': { bgcolor: ARANCIO, color: '#fff', fontWeight: 700 } };

/** Pulsante quadrato a sola icona. Lo span fa comparire il suggerimento anche da disabilitato. */
function Quadrato({ titolo, sx, ...resto }) {
  return (
    <Tooltip title={titolo}>
      <span>
        <IconButton aria-label={titolo} {...resto}
                    sx={{ width: 40, height: 40, borderRadius: '8px', border: `1px solid ${BORDO}`, color: '#475569', ...sx }} />
      </span>
    </Tooltip>
  );
}

/**
 * Barra in alto, dal mockup del 25/09/2026. Desktop: una riga, profilo in fondo a
 * destra; sotto i 1280 px Excel ed Esporta restano a sola icona per non andare a capo.
 * Telefono: logo, ricerca e profilo sopra; filtri e azioni come pulsanti quadrati sotto.
 */
export default function TopBar({ filters, setFilters, sortKey, setSortKey, selected, onClear, onRefresh,
                                 onExportSelected, isMobile, me, onLogout, onCambiaPassword, onGestisciUtenti,
                                 nRichieste = 0, onRichieste }) {
  const [anchor, setAnchor] = useState(null);
  const [userAnchor, setUserAnchor] = useState(null);
  const compatto = useMediaQuery('(max-width:1279px)');
  const iniziali = (me?.nome || '?').split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();
  // permessi decisi dal server: chi non vede il voto non ordina né filtra per voto o stato,
  // e chi vede solo i propri impianti non filtra per concessionaria
  const vedeVoto = me?.permessi?.vedeVoto !== false;
  const vedeTutto = me?.permessi?.vedeTutto !== false;
  const ordinamenti = Object.entries(SORTS).filter(([k]) => vedeVoto || !['voto-desc', 'voto-asc', 'da-rivedere'].includes(k));
  const chiSono = me?.azienda && me.azienda.tipo !== 'interna'
    ? me.azienda.nome : (me?.amministratore ? 'amministratore' : 'operatore');
  const nFilters = (filters.concessionarie?.length ? 1 : 0) + (filters.votoMin != null ? 1 : 0)
                 + (filters.stato ? 1 : 0) + (filters.tags?.length ? 1 : 0);
  const toggle = (arr = [], v) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

  /* ---- i pezzi della barra ---- */
  const ricerca = (
    <TextField size="small" placeholder="Cerca titolo, via, numero o ID…" value={filters.search || ''}
               onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
               sx={{ flex: '1 1 200px', minWidth: 140, maxWidth: isMobile ? 'none' : 380,
                     '& .MuiOutlinedInput-root': CONTROLLO }}
               InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
  );
  const apriFiltri = (e) => setAnchor(e.currentTarget);
  const filtri = (
    <Badge badgeContent={nFilters} sx={BADGE}>
      {isMobile
        ? <Quadrato titolo="Filtri e ordinamento" onClick={apriFiltri} sx={{ color: BLU }}><FilterList /></Quadrato>
        : <Button variant="outlined" startIcon={<FilterList />} onClick={apriFiltri} sx={BOTTONE}>Filtri</Button>}
    </Badge>
  );
  const ordina = !isMobile && (
    <FormControl size="small" sx={{ minWidth: 190, '& .MuiOutlinedInput-root': CONTROLLO }}>
      <InputLabel>Ordina per</InputLabel>
      <Select label="Ordina per" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
        {ordinamenti.map(([k, s]) => <MenuItem key={k} value={k}>{s.label}</MenuItem>)}
      </Select>
    </FormControl>
  );
  const titoloExcel = `Esporta i selezionati in Excel (.xlsx): titolo, ${vedeVoto ? 'voto, ' : ''}indirizzo, Street View, coordinate — nell'ordinamento attivo`;
  const excel = isMobile || compatto
    ? <Quadrato titolo={titoloExcel} disabled={!selected.size} onClick={onExportSelected}><IconaFoglio spento={!selected.size} /></Quadrato>
    : (
      <Tooltip title={titoloExcel}>
        <span>
          <Button variant="outlined" disabled={!selected.size} startIcon={<IconaFoglio spento={!selected.size} />}
                  onClick={onExportSelected} sx={BOTTONE}>
            {`Excel selezionati${selected.size ? ` (${selected.size})` : ''}`}
          </Button>
        </span>
      </Tooltip>
    );
  const titoloEsporta = vedeTutto ? 'Esporta tutto il database (CSV)'
    : me?.azienda?.tipo === 'pianificazione' ? 'Esporta gli impianti che consulti (CSV)' : 'Esporta i tuoi impianti (CSV)';
  const esportaTutti = () => window.open(wsurl + '/csv', '_blank');
  const esporta = isMobile || compatto
    ? <Quadrato titolo={titoloEsporta} onClick={esportaTutti} sx={{ color: BLU }}><IosShare /></Quadrato>
    : (
      <Tooltip title={titoloEsporta}>
        <Button variant="outlined" startIcon={<IosShare />} onClick={esportaTutti} sx={BOTTONE}>Esporta tutti</Button>
      </Tooltip>
    );
  const pulisci = <Quadrato titolo="Pulisci selezione" disabled={!selected.size} onClick={onClear}><CancelPresentation fontSize="small" /></Quadrato>;
  const aggiorna = <Quadrato titolo="Aggiorna dal server" onClick={onRefresh}><Refresh fontSize="small" /></Quadrato>;
  // ml auto: il profilo sta sempre in fondo a destra
  const profilo = (
    <Tooltip title={me ? `${me.nome} — ${chiSono}` : ''}>
      <IconButton onClick={(e) => setUserAnchor(e.currentTarget)} aria-label="Profilo e impostazioni" sx={{ ml: 'auto', p: 0 }}>
        {/* il numero sull'avatar: richieste di eliminazione che aspettano un amministratore */}
        <Badge badgeContent={nRichieste} overlap="circular" sx={BADGE}>
          <Avatar sx={{ width: 40, height: 40, fontSize: 15, fontWeight: 700, bgcolor: me?.amministratore ? BLU : '#64748b' }}>
            {iniziali}
          </Avatar>
        </Badge>
      </IconButton>
    </Tooltip>
  );

  return (
    <AppBar position="sticky" color="inherit" elevation={0} sx={{ borderBottom: `1px solid ${BORDO}`, bgcolor: 'white' }}>
      {isMobile ? (
        <Stack spacing={1} sx={{ px: 2, py: 1.25 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Logo compatto />
            {ricerca}
            {profilo}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {filtri}{excel}{esporta}{pulisci}{aggiorna}
          </Stack>
        </Stack>
      ) : (
        <Toolbar sx={{ gap: 1.25, py: 1.25, flexWrap: 'wrap' }}>
          <Box sx={{ mr: 0.75 }}><Logo /></Box>
          {ricerca}{filtri}{ordina}{excel}{esporta}{pulisci}{aggiorna}
          {profilo}
        </Toolbar>
      )}

      {/* ---- menu del profilo ---- */}
      <Menu anchorEl={userAnchor} open={!!userAnchor} onClose={() => setUserAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { mt: 1, borderRadius: 3, minWidth: 260, border: `1px solid ${BORDO}`,
                                boxShadow: '0 12px 32px rgba(15,23,42,.14)' } }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, pt: 1, pb: 1.5 }}>
          <Avatar sx={{ width: 40, height: 40, fontSize: 15, fontWeight: 700, bgcolor: '#e8eefc', color: BLU }}>{iniziali}</Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>{me?.nome}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">{me?.username} · {chiSono}</Typography>
            {me?.azienda?.accessoAl && (
              <Typography variant="caption" color="text.secondary" display="block">
                Accesso fino al {me.azienda.accessoAl.split('-').reverse().join('/')}
              </Typography>
            )}
          </Box>
        </Stack>
        {me?.amministratore && (
          <MenuItem onClick={() => { setUserAnchor(null); onGestisciUtenti(); }} sx={{ py: 1.1 }}>
            <ListItemIcon><PeopleAltOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>Aziende e utenti</ListItemText>
          </MenuItem>
        )}
        {me?.amministratore && nRichieste > 0 && (
          <MenuItem onClick={() => { setUserAnchor(null); onRichieste(); }} sx={{ py: 1.1 }}>
            <ListItemIcon><DeleteSweepOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>Richieste di eliminazione</ListItemText>
            <Chip size="small" label={nRichieste} sx={{ ml: 1.5, height: 20, bgcolor: ARANCIO, color: '#fff', fontWeight: 700 }} />
          </MenuItem>
        )}
        <MenuItem onClick={() => { setUserAnchor(null); onCambiaPassword(); }} sx={{ py: 1.1 }}>
          <ListItemIcon><LockOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>Cambia password</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setUserAnchor(null); onLogout(); }} sx={{ py: 1.1 }}>
          <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error' }}>Esci</ListItemText>
        </MenuItem>
      </Menu>

      <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}
               anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 2, width: 340 }}>
          {isMobile && (
            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel>Ordina per</InputLabel>
              <Select label="Ordina per" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                {ordinamenti.map(([k, s]) => <MenuItem key={k} value={k}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {vedeTutto && (<>
          <Typography variant="subtitle2" gutterBottom>Concessionaria</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1.5 }}>
            {CONCESSIONARIE.map(c => (
              <Chip key={c} label={c} size="small"
                    color={filters.concessionarie?.includes(c) ? 'primary' : 'default'}
                    variant={filters.concessionarie?.includes(c) ? 'filled' : 'outlined'}
                    onClick={() => setFilters(f => ({ ...f, concessionarie: toggle(f.concessionarie, c) }))} />
            ))}
          </Stack>
          <Divider sx={{ my: 1 }} />
          </>)}
          {vedeVoto && (<>
          <Typography variant="subtitle2" gutterBottom>Voto minimo</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1.5 }}>
            {[null, 4, 6, 7, 8, 9].map(v => (
              <Chip key={String(v)} label={v == null ? 'Tutti' : `V${v}+`} size="small"
                    color={filters.votoMin === v ? 'primary' : 'default'}
                    variant={filters.votoMin === v ? 'filled' : 'outlined'}
                    onClick={() => setFilters(f => ({ ...f, votoMin: v }))} />
            ))}
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" gutterBottom>Stato</Typography>
          <Stack direction="row" gap={0.5} sx={{ mb: 1.5 }}>
            {[[null, 'Tutti'], ['revisionato', 'Revisionati'], ['da rivedere', 'Da rivedere']].map(([v, l]) => (
              <Chip key={l} label={l} size="small"
                    color={filters.stato === v ? 'primary' : 'default'}
                    variant={filters.stato === v ? 'filled' : 'outlined'}
                    onClick={() => setFilters(f => ({ ...f, stato: v }))} />
            ))}
          </Stack>
          <Divider sx={{ my: 1 }} />
          </>)}
          <Typography variant="subtitle2" gutterBottom>Caratteristiche</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {TAGS.map(t => (
              <Chip key={t} label={t} size="small"
                    color={filters.tags?.includes(t) ? 'primary' : 'default'}
                    variant={filters.tags?.includes(t) ? 'filled' : 'outlined'}
                    onClick={() => setFilters(f => ({ ...f, tags: toggle(f.tags, t) }))} />
            ))}
          </Stack>
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button size="small" onClick={() => setFilters(f => ({ search: f.search, concessionarie: [], votoMin: null, stato: null, tags: [] }))}>
              Azzera filtri
            </Button>
          </Stack>
        </Box>
      </Popover>
    </AppBar>
  );
}
